import { Location } from 'history'
import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * @description: useNavigation hook is a replacement for redux push dispatch to programmatically navigate.
 */
const useNavigation = () => {
  const navigate = useNavigate()
  const { pathname: currentPath, search, state } = useLocation()

  const pushInto = useCallback(
    (segment: string | Partial<Location>, state: Record<never, never> = {}) => {
      navigate(`${currentPath}${segment}`, { state })
    },
    [currentPath, navigate]
  )
  const push = useCallback(
    (path: string | Partial<Location>, state: Record<never, never> = {}) =>
      navigate(path, { state }),
    [navigate]
  )

  const replace = useCallback(
    (path: string | Partial<Location>, state: Record<never, never> = {}) => {
      navigate(path, { state, replace: true })
    },
    [navigate]
  )

  const pushReplace = useCallback(
    (segment: string) => {
      const segments = currentPath.split('/')
      segments.pop()
      segments.push(segment)
      navigate(segments.join('/'))
    },
    [currentPath, navigate]
  )

  return {
    /**
     * @param path: string - path to navigate to
     * @param state object - information to pass to the new route
     * @description: programmatically navigate to a path. Completely replaces the current path. Add a new entry to the history if the path is different from the current path.
     */
    push,
    /**
     * @param path: string - path to navigate to
     * @description: programmatically navigate to a path. Completely replaces the current path. Replaces the current path to the new one and leaving it outside the history
     */
    replace,
    /**
     * @description Navigates to another sub-route specified by the segment argument.
     * @example pushInto('/add') will navigate to 'report/reportId/add' if currently on 'report/reportId'.
     */
    pushInto,
    /**
     * @description: Replace the last segment of the path with another
     * @example pushReplace('team/new', '123') will navigate to /team/123
     * @param
     */
    pushReplace,
    /**
     * @description The current path of the dashboard.
     */
    pathname: currentPath,
    /**
     * @description The current query search value in the path.
     * @example: /team?show=true will yield show=true
     */
    search,
    /**
     * @description The state passed from the previous page
     */
    state
  }
}

export default useNavigation
